import {observer} from 'mobx-react-lite'
import React from 'react'
import {DragDataObject} from '@common/drag-data'
import {Id, Identifiable, RowConfig} from '@components/common/sandbox/list-view/types'
import {DraggableNative} from '@components/draggable-native'
import { useListStore } from '../list-store'
import { TwRow } from '../tw-components'

export interface RowProps<TData extends Identifiable> extends React.HTMLAttributes<HTMLDivElement> {
  id: Id,
  rowConfig: RowConfig<TData>,
  neighbours: RowConfig<Identifiable>[],
  children: React.ReactNode,
  transferData?: DragDataObject | undefined,
  draggable: boolean,
  nestedLevel: number
}

const staticGetDragImageOffset = () => ({x: 12, y: 2})

export const ListRow = observer(<TData extends Identifiable>(props: RowProps<TData>) => {
  const {
    id,
    rowConfig,
    neighbours,
    children,
    transferData,
    draggable,
    nestedLevel,
    ...twRowProps
  } = props
  const store = useListStore()
  const enterCounter = React.useRef(0)

  const handleDragStarted = (_: HTMLElement, e: React.DragEvent) => {
    const [done, total] = rowConfig.children?.reduce((acc, child) => {
      return [acc[0] + (store.isDone(child) ? 1 : 0), acc[1] + 1]
    }, [0, 0]) || [0, 0]
    store.showDragGhost(store.selectName(rowConfig.data), total, done)
    /* Find neighbours */
    const draggedIndex = neighbours.findIndex(row => row.data.id === id)
    const aboveNeighbourId =
      draggedIndex > 0 ? neighbours[draggedIndex - 1].data.id : null
    const belowNeighbourId =
      draggedIndex < neighbours.length - 1
        ? neighbours[draggedIndex + 1].data.id
        : 'none'

    store.onDragStarted(id, aboveNeighbourId, belowNeighbourId, e)
  }

  /**
   * Handle drag over event. It ensures that the drag over event is only handled once.
   * @param event
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (enterCounter.current) {
      store.onDragOver(event, id, nestedLevel)
    }
    event.preventDefault()
  }

  /**
   * Handle drag enter event. It ensures that the drag enter event is only handled once
   * and also pass the ID and nested level to the store method.
   * @param event
   */
  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    enterCounter.current++
    if (enterCounter.current === 1) {
      store.onDragEnter(event, id, nestedLevel)
    }
  }

  const handleDragEnded = () => {
    store.hideDragGhost();
    store.onDragEnded()
  }

  /**
   * Handle drag leave event. It ensures that the drag leave event is only handled once
   * and also pass the ID to the store method.
   * @param event
   */
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    enterCounter.current--
    if (enterCounter.current === 0) {
      store.onDragLeave(event, id)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    store.onDrop(event)
  }

  const createGhost = () => {
    return store.dragGhostElement
  }

  return <DraggableNative
    draggable={draggable}
    transfer={transferData}
    onDragStarted={handleDragStarted}
    onDragEnded={handleDragEnded}
    onDragOver={handleDragOver}
    onDragEnter={handleDragEnter}
    onDragLeave={handleDragLeave}
    onDrop={handleDrop}
    createGhost={createGhost}
    dontDeleteGhost
    noStyleEffects
    getDragImageOffset={staticGetDragImageOffset}
  >
      <TwRow {...twRowProps} $active={store.getIsActive(id) || store.draggedId === id}>
        {children}
      </TwRow>
  </DraggableNative>
})